<template>
  <div class="members-layout">
    <!-- 主内容区 -->
    <main class="members-content">
      <!-- 标题和搜索区域 -->
      <div class="content-header">
        <div class="header-left">
          <h1 class="page-title">会员管理</h1>
          <button class="add-member-button" @click="openAddMemberForm">
            + 添加会员
          </button>
        </div>
        <div class="search-controls">
          <div class="search-bar">
            <input
              v-model="searchKeyword"
              type="text"
              placeholder="搜索会员姓名或手机号..."
              class="search-input"
              @keyup.enter="handleSearch"
            />
            <button class="search-button" @click="handleSearch">搜索</button>
          </div>
          <button class="reset-button" @click="resetFilters">重置筛选</button>
        </div>
      </div>

      <!-- 会员统计 -->
      <div class="members-stats">
        <div class="stat-card">
          <div class="stat-label">会员总数</div>
          <div class="stat-value">{{ total }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">订单总额</div>
          <div class="stat-value">¥{{ totalOrderAmount }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">新增会员</div>
          <div class="stat-value">{{ newMembersCount }}</div>
        </div>
      </div>

      <!-- 会员表格 -->
      <div class="members-table-container">
        <div v-if="loading" class="loading-container">
          <div class="loading-spinner"></div>
          <div class="loading-text">加载中...</div>
        </div>
        <table v-else class="members-table">
          <thead>
            <tr>
              <th>会员信息</th>
              <th>手机号</th>
              <th class="sortable-header" @click="toggleOrderCountSort">
                订单数量
                <span class="sort-indicator">
                  <span v-if="orderCountSortOrder === 'asc'" class="sort-asc">↑</span>
                  <span v-else-if="orderCountSortOrder === 'desc'" class="sort-desc">↓</span>
                  <span v-else class="sort-none">↕</span>
                </span>
              </th>
              <th class="sortable-header" @click="toggleOrderAmountSort">
                订单总额
                <span class="sort-indicator">
                  <span v-if="orderAmountSortOrder === 'asc'" class="sort-asc">↑</span>
                  <span v-else-if="orderAmountSortOrder === 'desc'" class="sort-desc">↓</span>
                  <span v-else class="sort-none">↕</span>
                </span>
              </th>
              <th>注册时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="paginatedMembers.length === 0">
              <td colspan="6" class="no-data">暂无会员数据</td>
            </tr>
            <tr v-for="member in paginatedMembers" :key="member.id">
              <td class="member-info">
                <div class="member-name">{{ member.name }}</div>
              </td>
              <td class="member-phone">{{ member.phone }}</td>
              <td class="order-count-cell">
                <span class="order-count">{{ member.orderCount }}</span>
              </td>
              <td class="order-amount-cell">¥{{ member.orderAmount }}</td>
              <td class="register-date-cell">{{ member.registerDate }}</td>
              <td class="action-cell">
                <button class="edit-button" @click="openEditMemberForm(member)">
                  修改
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分页 -->
      <div class="pagination">
        <div class="pagination-info">
          显示 {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, total) }}
          共 {{ total }} 条
        </div>
        <div class="pagination-controls">
          <button
            class="page-btn"
            :disabled="currentPage === 1"
            @click="currentPage--"
          >
            上一页
          </button>
          <span class="page-number">{{ currentPage }} / {{ totalPages }}</span>
          <button
            class="page-btn"
            :disabled="currentPage === totalPages"
            @click="currentPage++"
          >
            下一页
          </button>
        </div>
      </div>
    </main>

    <!-- 会员表单弹窗 -->
    <MemberForm
      :visible="showMemberForm"
      :member="selectedMember"
      @close="closeMemberForm"
      @submit="handleMemberSubmit"
    />
  </div>
</template>

<script>
import MemberForm from './MemberForm.vue'
import { memberApi } from '../api/index.js'

export default {
  name: 'MembersView',
  components: {
    MemberForm
  },
  props: {
    userInfo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      searchKeyword: '',
      currentPage: 1,
      orderCountSortOrder: null, // null, 'asc', 'desc'
      orderAmountSortOrder: null, // null, 'asc', 'desc'
      pageSize: 15,
      showMemberForm: false,
      selectedMember: null,
      members: [],
      loading: false,
      total: 0 // 总记录数
    }
  },
  computed: {
    // 由于现在通过API直接获取分页数据，paginatedMembers直接返回members
    paginatedMembers() {
      return this.members || []
    },
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    },
    totalOrderAmount() {
      // 由于API返回的数据中没有订单统计，这里显示为0
      // 实际项目中需要另外的API来获取订单统计
      return 0
    },
    newMembersCount() {
      const currentMonth = new Date().getMonth()
      const currentYear = new Date().getFullYear()

      return this.members.filter(member => {
        if (!member.created_at) return false
        const registerDate = new Date(member.created_at)
        return registerDate.getMonth() === currentMonth && registerDate.getFullYear() === currentYear
      }).length
    }
  },
  methods: {
    // 加载会员列表
    async loadMembers() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize
        }

        // 添加搜索参数
        if (this.searchKeyword.trim()) {
          params.name = this.searchKeyword.trim()
        }

        const result = await memberApi.getMemberList(params)

        if (result.code === 0 && result.data) {
          // 转换数据格式
          this.members = result.data.list.map(member => ({
            id: member.id,
            name: member.name,
            phone: member.phone,
            gender: this.mapGenderToChinese(member.gender),
            address: member.address || '无地址',
            registerDate: member.created_at ? new Date(member.created_at).toISOString().split('T')[0] : '未知',
            orderCount: 0, // API暂不提供，设为默认值
            orderAmount: 0  // API暂不提供，设为默认值
          }))
          this.total = result.data.total || 0
          console.log('会员列表加载成功:', this.members)
        } else {
          console.error('获取会员列表失败:', result.msg)
          alert(result.msg || '获取会员列表失败')
        }
      } catch (error) {
        console.error('获取会员列表异常:', error)
        alert('获取会员列表失败，请检查网络连接')
      } finally {
        this.loading = false
      }
    },

    // 性别映射：后端字段 -> 前端显示
    mapGenderToChinese(gender) {
      const genderMap = {
        'male': '男',
        'female': '女',
        'other': '未知'
      }
      return genderMap[gender] || '未知'
    },

    // 性别映射：前端显示 -> 后端字段
    mapGenderToEnglish(gender) {
      const genderMap = {
        '男': 'male',
        '女': 'female',
        '未知': 'other',
        '': 'other'
      }
      return genderMap[gender] || 'other'
    },

    toggleOrderCountSort() {
      // 由于API没有提供订单统计，暂时禁用排序功能
      alert('排序功能暂时不可用')
    },
    toggleOrderAmountSort() {
      // 由于API没有提供订单统计，暂时禁用排序功能
      alert('排序功能暂时不可用')
    },
    async handleSearch() {
      this.currentPage = 1 // 重置到第一页
      await this.loadMembers() // 重新加载数据
    },
    resetFilters() {
      this.searchKeyword = ''
      this.currentPage = 1
      this.loadMembers() // 重新加载数据
    },
    openAddMemberForm() {
      this.selectedMember = null
      this.showMemberForm = true
    },
    openEditMemberForm(member) {
      this.selectedMember = member
      this.showMemberForm = true
    },
    closeMemberForm() {
      this.showMemberForm = false
      this.selectedMember = null
    },
    async handleMemberSubmit(memberData) {
      try {
        const submitData = {
          ...memberData,
          gender: this.mapGenderToEnglish(memberData.gender)
        }

        if (this.selectedMember) {
          // 修改会员
          const result = await memberApi.updateMember(submitData)
          if (result.code === 0) {
            alert('会员信息更新成功')
            await this.loadMembers() // 重新加载列表
          } else {
            alert(result.msg || '更新会员失败')
          }
        } else {
          // 添加会员
          const result = await memberApi.addMember(submitData)
          if (result.code === 0) {
            alert('会员添加成功')
            await this.loadMembers() // 重新加载列表
          } else {
            alert(result.msg || '添加会员失败')
          }
        }
        this.currentPage = 1
      } catch (error) {
        console.error('保存会员失败:', error)
        alert('保存失败，请检查网络连接')
      }
    }
  },
  watch: {
    currentPage() {
      this.loadMembers()
    }
  },
  mounted() {
    this.loadMembers()
  }
}
</script>

<style scoped>
.members-layout {
  height: 100vh;
  background-color: #f5f5f5;
  font-family: 'Microsoft YaHei', sans-serif;
}

/* 主内容区 */
.members-content {
  flex: 1;
  height: 100vh;
  padding: 20px;
  overflow-y: auto;
  background-color: white;
  box-sizing: border-box;
  min-width: 0; /* 防止flex子项溢出 */
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
}

.add-member-button {
  background-color: #52c41a;
  color: white;
  border: none;
  height: 36px;
  padding: 0 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.add-member-button:hover {
  background-color: #73d13d;
}

.search-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 10px;
}

.search-input {
  border: 1px solid #d9d9d9;
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 14px;
  width: 300px;
  outline: none;
  transition: all 0.2s;
}

.search-input:focus {
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24,144,255,0.2);
}

.search-button, .reset-button {
  background-color: #1890ff;
  color: white;
  border: none;
  height: 32px;
  padding: 0 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.search-button:hover, .reset-button:hover {
  background-color: #4096ff;
}

.reset-button {
  background-color: #f5f5f5;
  color: #666;
  border: 1px solid #d9d9d9;
}

.reset-button:hover {
  background-color: #e6f7ff;
  border-color: #1890ff;
  color: #1890ff;
}

/* 统计卡片 */
.members-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.stat-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.stat-card:nth-child(2) {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.stat-card:nth-child(3) {
  background: linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%);
}

.stat-label {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
}

/* 表格 */
.members-table-container {
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  width: 100%;
}

.members-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.members-table th {
  background-color: #fafafa;
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e8e8e8;
  font-size: 14px;
  white-space: nowrap;
}

.members-table th:nth-child(1) {
  width: 25%;
}

.members-table th:nth-child(2) {
  width: 20%;
}

.members-table th:nth-child(3) {
  width: 15%;
}

.members-table th:nth-child(4) {
  width: 20%;
}

.members-table th:nth-child(5) {
  width: 15%;
}

.members-table th:nth-child(6) {
  width: 10%;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  position: relative;
  transition: background-color 0.2s;
}

.sortable-header:hover {
  background-color: #f0f0f0;
}

.sort-indicator {
  margin-left: 8px;
  font-size: 12px;
  color: #666;
}

.sort-asc {
  color: #52c41a;
}

.sort-desc {
  color: #f5222d;
}

.sort-none {
  color: #999;
}

.members-table td {
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
}

.members-table tr:hover {
  background-color: #f9f9f9;
}

.member-info {
  min-width: 120px;
}

.member-name {
  font-weight: 500;
  color: #333;
  line-height: 1.4;
}

.member-phone {
  color: #666;
  font-size: 14px;
  font-family: monospace;
}

.order-count-cell {
  text-align: center;
  font-weight: 600;
}

.order-count {
  background-color: #e6f7ff;
  color: #1890ff;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.order-amount-cell {
  text-align: right;
  font-weight: 600;
  color: #e74c3c;
}

.register-date-cell {
  color: #666;
  font-size: 13px;
}

.action-cell {
  text-align: center;
}

.edit-button {
  background-color: #1890ff;
  color: white;
  border: none;
  height: 28px;
  padding: 0 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.edit-button:hover {
  background-color: #4096ff;
}

.no-data {
  text-align: center;
  color: #999;
  padding: 40px;
  font-size: 14px;
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 20px;
  padding: 20px;
  background-color: #fafafa;
  border-radius: 8px;
  box-sizing: border-box;
}

.pagination-info {
  color: #666;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.page-btn {
  background-color: white;
  border: 1px solid #d9d9d9;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
}

.page-btn:hover:not(:disabled) {
  border-color: #1890ff;
  color: #1890ff;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-number {
  font-weight: 500;
  color: #333;
}

/* 加载状态样式 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #666;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #1890ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.loading-text {
  font-size: 14px;
  color: #999;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>